import config from '../config'
import getUniqueArrayByKey from '../helpers/getUniqueArrayByKey'

const BASE_URL = config.api.url

const request = async (url, method, params) => {
  const token = localStorage.getItem('token')
  const isFormData = params instanceof FormData
  const query = method === 'GET' && params ? `?${new URLSearchParams(params).toString()}` : ''
  const res = await fetch(
    `${BASE_URL}/${url}${query}`,
    {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(method !== 'GET' ? {body: isFormData ? params : JSON.stringify(params)} : null),
      // signal: AbortSignal.timeout(3000),
    },
  ).catch(err => {
    return {ok: false}
  })
  if (res.ok) {
    if (res.headers.get('Content-Type') === 'application/json') {
      return await res.json()
    }
    return true
  } else if (res.status === 403 && res.headers.get('Content-Type') === 'application/json') {
    const json = await res.json()
    if (json.status === 'employee_suspended' || json.status === 'employee_blocked') {
      localStorage.setItem('token', '')
      localStorage.setItem('user', '')
      window.location.href = '/'
    }
  }
  return null
}

const sendRequest = async (url, method, params) => {
  const token = localStorage.getItem('token')
  const isFormData = params instanceof FormData
  const query = method === 'GET' && params ? `?${new URLSearchParams(params).toString()}` : ''
  const res = await fetch(
    `${BASE_URL}/${url}${query}`,
    {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(method !== 'GET' ? {body: isFormData ? params : JSON.stringify(params)} : null),
    },
  )
  if (res.headers.get('Content-Type') === 'application/json') {
    return await res.json()
  }
  return await res.text()
}

export const getPlaces = async (companyId, params) => {
  const res = await request(`company/${companyId}/places`, 'GET', params)  
  return res
}

export const getCompanyJobTypes = async (companyId) => {
  const res = await request(`company/${companyId}/job-types`, 'GET')
  return res
}

export const getCompanyCustomCategories = async (companyId) => {
  const res = await request(`company/${companyId}/custom_categories`, 'GET')
  return res
}

export const updatePlace = async (companyId, placeId, data) => {
  const res = await request(`company/${companyId}/places/update/${placeId}`, 'PATCH', data)
  return res
}

export const getCompanyInfo = async (companyId) => {
  const res = await request(`company/${companyId}/edit`, 'GET')
  return res
}

export const getCompanyHolidays = async (companyId, params) => {
  const res = await request(`company/${companyId}/holidays`, 'GET', params)
  return res
}

export const getCompanyWorkTimeSettings = async (companyId) => {
  const res = await request(`company/${companyId}/work-time`, 'GET')
  return res
}

export const getCompanyEmployeesAll = async (companyId) => {
  const res = await request(`company/${companyId}/employees/all`, 'GET')
  return res
}

export const getCompanyShifts = async (companyId, params) => {
  const res = await request(`company/${companyId}/shift`, 'GET', params)
  return res
}

export const getCompanyTimeOffRequests = async (companyId, params) => {
  const res = await request(`company/${companyId}/time-off/all/policy/all/request-behalf/employee/all`, 'GET', params)
  return res
}

export const getCompanyTimeOffPolicies = async (companyId) => {
  const res = await request(`company/${companyId}/time-off/all/policy`, 'GET')
  return res
}

export const getCompanyTimeOffs = async (companyId) => {
  const res = await request(`company/${companyId}/time-off`, 'GET')
  return res?.time_offs || []
}

export const createRequest = async (companyId, timeOffId, policyId, data) => {
  const formData = new FormData()
  data.employees.forEach(employeeId => formData.append('employees[]', employeeId))
  formData.append('from', data.from)
  formData.append('to', data.to)
  formData.append('note', data.note || '')
  if (data.documents && Array.isArray(data.documents)) {
    data.documents.forEach(file => {
      formData.append('documents[]', file)
    })
  }
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/request-behalf/store`, 'POST', formData)
  return res
}

export const updateRequest = async (companyId, timeOffId, policyId, requestId, data) => {
  const formData = new FormData()

  data.employees.forEach(employeeId => {
    formData.append('employees[]', employeeId)
  })

  formData.append('from', data.from)
  formData.append('to', data.to)
  formData.append('note', data.note || '')

  if (Array.isArray(data.documents)) {
    data.documents.forEach((document, index) => {
      if (document instanceof File) {
        formData.append(`documents[]`, document)
      } else if (document.id) {
        formData.append(`document_ids[]`, document.id)
      }
    })
  }
  
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/request-behalf/${requestId}`, 'POST', formData)
  return res
}

export const createAdjustBalance = async (companyId, timeOffId, policyId, data) => {
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/adjust-balance/store`, 'POST', data)
  return res
}

export const createAdjustTimeUsed = async (companyId, timeOffId, policyId, data) => {
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/adjust-time-used/store`, 'POST', data)
  return res
}

export const updateRequestStatus = async (companyId, timeOffId, policyId, requestId, data) => {
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/request-behalf/${requestId}/status`, 'POST', data)
  return res
}

export const updateRequestStatusBulk = async (companyId, data) => {
  const res = await request(`company/${companyId}/time-off/update-request-status`, 'POST', data)
  return res
}

export const getEmployeePolicies = async (companyId, employeeId) => {
  const res = await request(`company/${companyId}/time-off/all/policy/employee/${employeeId}`)
  if (Array.isArray(res?.policies)) {
    return res.policies
  }
  return []
}

export const getPolicyEmployee = async (companyId, timeOffId, policyId, employeeId) => {
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/employee/${employeeId}`)
  return res
}

export const getTimeOffEmployeeRequests = async (companyId, employeeId) => {
  const res = await request(`company/${companyId}/time-off/all/policy/all/request-behalf/employee/${employeeId}`)
  if (Array.isArray(res?.request_behalf)) {
    return res.request_behalf
  }
  return []
}

export const removePolicyActivity = async (companyId, timeOffId, policyId, employeeId, activityId) => {
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/employee/${employeeId}/activity/${activityId}`, 'DELETE')
  return res
}

export const updatePolicyActivity = async (companyId, timeOffId, policyId, employeeId, activityId, data) => {
  const res = await request(`company/${companyId}/time-off/${timeOffId}/policy/${policyId}/employee/${employeeId}/activity/${activityId}`, 'PATCH', data)
  return res
}

export const publishSchedule = async (companyId, data) => {
  const res = await request(`company/${companyId}/shift/publish`, 'POST', data)
  return res
}

export const notifySchedule = async (companyId, data) => {
  const res = await request(`company/${companyId}/shift/notify`, 'POST', data)
  return res
}

export const scheduleChangesLog = async (companyId, data) => {
  const res = await request(`company/${companyId}/shift/changes-log`, 'POST', data)
  return res
}

export const getCompanySkills = async (companyId) => {
  const res = await request(`company/${companyId}/specialities`, 'GET')
  return res
}

export const getAIGenerationSettings = async (companyId) => {
  const res = await request(`company/${companyId}/ai/schedule-generation-settings`, 'GET')
  return res
}

export const updateAIGenerationSettings = async (companyId, data) => {
  const res = await request(`company/${companyId}/ai/schedule-generation-settings`, 'POST', data)
  return res
}

export const generateAIShiftEvents = async (companyId, shiftId, data) => {
  const res = await request(`company/${companyId}/shift/${shiftId}/generate-ai-times`, 'PATCH', data)
  return res
}

export const updateDemandToolsForDate = async (companyId, shiftId, data) => {
  const res = await request(`company/${companyId}/shift/${shiftId}/demand-tools/update`, 'POST', data)
  return res
}

export const savePlaceNote = async (companyId, placeId, data) => {
  const res = await request(`company/${companyId}/places/note/${placeId}}`, 'PATCH', data)
  return res
}

export const generateAITimeSheet = async (companyId, data) => {
  const res = await request(`company/${companyId}/sheet/ai`, 'POST', data)
  return res
}

export const getGeneratedTimeSheet = async (companyId, generationId) => {
  const res = await request(`company/${companyId}/sheet/ai/${generationId}`, 'GET')
  return res
}

export const getGeneratedTimeSheets = async (companyId, date) => {
  const res = await request(`company/${companyId}/sheet/ai?month=${date}`, 'GET')
  return res
}

export const removeGeneratedTimeSheet = async (companyId, generationId) => {
  const res = await request(`company/${companyId}/sheet/ai/${generationId}`, 'DELETE')
  return res
}

export const getWorkSessionGallery = async (workTimeId) => {
  const res = await request(`work-session/gallery/${workTimeId}`, 'GET')
  return res
}

export const downloadGeneratedAITimeSheet = async (companyId, generationId, params) => {
  const res = await request(`company/${companyId}/sheet/ai/${generationId}/integration`, 'POST', params)
  return res
}

export const getPlaceFolders = async (companyId, placeId) => {
  const res = await request(`company/${companyId}/places/${placeId}/folders`, 'GET')
  return res
}

export const getPlaceFolder = async (companyId, placeId, folderId) => {
  const res = await request(`company/${companyId}/places/${placeId}/folders/${folderId}`, 'GET')
  return res
}

export const createPlaceFolder = async (companyId, placeId, data) => {
  const res = await request(`company/${companyId}/places/${placeId}/folders`, 'POST', data)
  return res
}

export const updatePlaceFolder = async (companyId, placeId, folderId, data) => {
  const res = await request(`company/${companyId}/places/${placeId}/folders/${folderId}`, 'PUT', data)
  return res
}

export const deletePlaceFolder = async (companyId, placeId, folderId) => {
  const res = await request(`company/${companyId}/places/${placeId}/folders/${folderId}`, 'DELETE')
  return res
}

export const removePlaceDocument = async (companyId, placeId, folderId, documentId) => {
  const res = await request(`company/${companyId}/places/${placeId}/folders/${folderId}/documents/${documentId}`, 'DELETE')
  return res
}

export const updatePlaceDocumentAccess = async (companyId, placeId, folderId, data) => {
  const res = await request(`company/${companyId}/places/${placeId}/folders/${folderId}/assign`, 'PUT', data)
  return res
}

export const getExpensesCompanyStats = async (companyId, params) => {
  const res = await request(`company/${companyId}/place-expenses/summary`, 'GET', params)
  return res
}

export const getPlaceExpenses = async (companyId, placeId, params) => {
  const res = await request(`company/${companyId}/places/${placeId}/expenses`, 'GET', params)
  return res
}

export const getCompanyExpenseTypes = async (companyId) => {
  const res = await request(`company/${companyId}/expense-types`, 'GET')
  return res
}

export const getCurrencies = async () => {
  const res = await request(`currencies`, 'GET')
  if (Array.isArray(res)) {
    return getUniqueArrayByKey(res, 'code')
  }
  return []
}

export const createPlaceExpense = async (companyId, placeId, data) => {
  const res = await request(`company/${companyId}/places/${placeId}/expenses`, 'POST', data)
  return res
}

export const updatePlaceExpense = async (companyId, placeId, expenseId, data) => {
  const res = await request(`company/${companyId}/places/${placeId}/expenses/${expenseId}`, 'POST', data)
  return res
}

export const deletePlaceExpense = async (companyId, placeId, expenseId) => {
  const res = await request(`company/${companyId}/places/${placeId}/expenses/${expenseId}`, 'DELETE')
  return res
}

export const getCustomers = async (companyId) => {
  const res = await request(`company/${companyId}/customers`, 'GET')
  return res
}

export const getSuppliers = async (companyId) => {
  const res = await request(`company/${companyId}/suppliers`, 'GET')
  return res
}

export const createSupplier = async (companyId, data) => {
  const res = await request(`company/${companyId}/suppliers`, 'POST', data)
  return res
}

export const getMaterials = async (companyId) => {
  const res = await request(`company/${companyId}/materials`, 'GET')
  return res
}

export const createMaterial = async (companyId, data) => {
  const res = await request(`company/${companyId}/materials`, 'POST', data)
  return res
}

export const getKioskBreaths = async (companyId, kioskId) => {
  const res = await request(`company/${companyId}/kiosk/${kioskId}/breaths`, 'GET')
  return res
}

export const restoreKioskBreaths = async (companyId, kioskId) => {
  const res = await request(`company/${companyId}/kiosk/${kioskId}/breaths`, 'DELETE')
  return res
}

export const updateWorkTime = async (companyId, workTimeId, params) => {
  const res = await request(`company/${companyId}/logbook/work-time/${workTimeId}`, 'PATCH', params)
  return res
}

export const getPlaceAttendance = async (companyId, placeId, params) => {
  const res = await request(`company/${companyId}/places/${placeId}/attendance`, 'GET', params)
  return res
}

export const getPlaceProfitability = async (companyId, placeId, params) => {
  const res = await request(`company/${companyId}/places/${placeId}/profitability`, 'GET', params)
  return res
}

export const finishTimerList = async (companyId, data) => {
  const res = await request(`company/${companyId}/timer/finish-list`, 'POST', data)
  return res
}

export const getInvoicesWorkTimes = async (companyId, params) => {
  const res = await request(`company/${companyId}/invoices/work-times`, 'GET', params)
  return res
}

export const getInvoicesExpenses = async (companyId, params) => {
  const res = await request(`company/${companyId}/invoices/expenses`, 'GET', params)
  return res
}

export const createInvoice = async (companyId, data) => {
  const res = await sendRequest(`company/${companyId}/invoices`, 'POST', data)
  return res
}

export const updateInvoice = async (companyId, invoiceId, data) => {
  const res = await sendRequest(`company/${companyId}/invoices/${invoiceId}`, 'PATCH', data)
  return res
}

export const getInvoice = async (companyId, invoiceId) => {
  const res = await request(`company/${companyId}/invoices/${invoiceId}`, 'GET')
  return res
}

export const getInvoices = async (companyId, params) => {
  const res = await request(`company/${companyId}/invoices`, 'GET', params)
  return res
}

export const removeInvoice = async (companyId, invoiceId) => {
  const res = await request(`company/${companyId}/invoices/${invoiceId}`, 'DELETE')
  return res
}

export const updateInvoiceStatus = async (companyId, invoiceId, data) => {
  const res = await request(`company/${companyId}/invoices/${invoiceId}/status`, 'PATCH', data)
  return res
}

export const getInvoiceSettings = async (companyId) => {
  const res = await request(`company/${companyId}/invoices/settings`, 'GET')
  return res
}

export const updateInvoiceSettings = async (companyId, data) => {
  const res = await request(`company/${companyId}/invoices/settings`, 'PATCH', data)
  return res
}

export const getCompanyIntegrations = async (companyId) => {
  const res = await request(`company/${companyId}/integrations`, 'GET')
  return res
}

export const getPlaceTargets = async (companyId, placeId) => {
  const res = await request(`company/${companyId}/places/${placeId}/targets`, 'GET')
  return res
}

export const updatePlaceTargets = async (companyId, placeId, data) => {
  const res = await request(`company/${companyId}/places/${placeId}/targets`, 'PATCH', data)
  return res
}

export const getPlaceEmployeeTargets = async (companyId, placeId) => {
  const res = await request(`company/${companyId}/places/${placeId}/employee-targets`, 'GET')
  return res
}

export const updatePlaceEmployeeTargets = async (companyId, placeId, data) => {
  const res = await request(`company/${companyId}/places/${placeId}/employee-targets`, 'PATCH', data)
  return res
}

export const getPlaceEmployees = async (companyId, placeId) => {
  const res = await request(`company/${companyId}/places/${placeId}/employees`, 'GET')
  return res
}

export const getCompanyActivityLog = async (companyId, params) => {
  const res = await request(`company/${companyId}/activity-log`, 'POST', params)
  return res
}

export const getUserFilters = async (companyId, type) => {
  const res = await request(`company/${companyId}/user-filters`, 'GET', {type: type})
  return res
}

export const saveUserFilters = async (companyId, data) => {
  const res = await request(`company/${companyId}/user-filters`, 'POST', data)
  return res
}

export const updateUserFilters = async (companyId, filterId, data) => {
  const res = await request(`company/${companyId}/user-filters/${filterId}`, 'PATCH', data)
  return res
}

export const updateUserFiltersStatus = async (companyId, filterId, data) => {
  const res = await request(`company/${companyId}/user-filters/${filterId}/status`, 'PATCH', data)
  return res
}

export const deleteUserFilters = async (companyId, filterId) => {
  const res = await request(`company/${companyId}/user-filters/${filterId}`, 'DELETE')
  return res
}

export const changeShiftTimeline = async (companyId, shiftId, data) => {
  const res = await request(`company/${companyId}/shift/${shiftId}/change/timeline`, 'PATCH', data)
  return res
}

export const addShiftTimeline = async (companyId, shiftId, params) => {
  const res = await request(`company/${companyId}/shift/${shiftId}/add/timeline`, 'PATCH', params)
  return res
}

export const getShifts = async (companyId, params) => {
  const res = await request(`company/${companyId}/shift`, 'GET', params)
  return res
}

export const addLogbookEntries = async (companyId, data) => {
  const res = await request(`company/${companyId}/logbook/add/bulk`, 'POST', data)
  return res
}

export const updateLogbookEntry = async (companyId, data) => {
  const res = await request(`company/${companyId}/logbook/edit`, 'POST', data)
  return res
}

export const getCompanyAssetsCategories = async (companyId) => {
  const res = await request(`company/${companyId}/assets/categories`, 'GET')
  return res
}

export const getAssetsOwnerships = async (companyId) => {
  const res = await request(`company/${companyId}/assets/ownerships`, 'GET')
  return res
}

export const getAssetsSuppliers = async (companyId) => {
  const res = await request(`company/${companyId}/assets/suppliers`, 'GET')
  return res
}

export const getAssetsAssigns = async (companyId) => {
  const res = await request(`company/${companyId}/assets/assigns`, 'GET')
  return res
}

export const createAsset = async (companyId, data) => {
  const res = await request(`company/${companyId}/assets`, 'POST', data)
  return res
}

export const updateAsset = async (companyId, assetId, data) => {
  const res = await request(`company/${companyId}/assets/${assetId}`, 'POST', data)
  return res
}

export const getCompanyAssets = async (companyId, params) => {
  const res = await request(`company/${companyId}/assets`, 'GET', params)
  return res
}

export const getAsset = async (companyId, assetId) => {
  const res = await request(`company/${companyId}/assets/${assetId}`, 'GET')
  return res
}

export const removeAsset = async (companyId, assetId) => {
  const res = await request(`company/${companyId}/assets/${assetId}`, 'DELETE')
  return res
}

export const getIikoExteralEmployees = async (companyId) => {
  const res = await request(`company/${companyId}/iiko/external-employees`, 'GET')
  return res
}

export const createMaintenancePlan = async (companyId, data) => {
  const res = await request(`company/${companyId}/maintenance-plans`, 'POST', data)
  return res
}

export const getMaintenancePlans = async (companyId, params) => {
  const res = await request(`company/${companyId}/maintenance-plans`, 'GET', params)
  return res
}

export const getMaintenancePlan = async (companyId, planId) => {
  const res = await request(`company/${companyId}/maintenance-plans/${planId}`, 'GET')
  return res
}

export const updateMaintenancePlan = async (companyId, planId, data) => {
  const res = await request(`company/${companyId}/maintenance-plans/${planId}`, 'PATCH', data)
  return res
}

export const removeMaintenancePlan = async (companyId, planId) => {
  const res = await request(`company/${companyId}/maintenance-plans/${planId}`, 'DELETE')
  return res
}

export const getMaintenanceAssets = async (companyId, params) => {
  const res = await request(`company/${companyId}/maintenance-plans/get-planned-maintenance-assets`, 'GET', params)
  return res
}

export const createMaintenanceRecord = async (companyId, data) => {
  const res = await request(`company/${companyId}/maintenance-record`, 'POST', data)
  return res
}

export const getMaintenanceRecords = async (companyId, params) => {
  const res = await request(`company/${companyId}/maintenance-record`, 'GET', params)
  return res
}

export const getCompanyApiKey = async (companyId) => {
  const res = await request(`admin/company/${companyId}/api-key`, 'GET')
  return res
}

export const generateCompanyApiKey = async (companyId) => {
  const res = await request(`admin/company/${companyId}/api-key`, 'POST')
  return res
}

export const revokeCompanyApiKey = async (companyId) => {
  const res = await request(`admin/company/${companyId}/api-key`, 'DELETE')
  return res
}

export const getDocumentTypesCategories = async (companyId) => {
  const res = await request(`company/${companyId}/documents-types-category`, 'GET')
  return res
}

export const createDocumentType = async (companyId, data) => {
  const res = await request(`company/${companyId}/documents-types`, 'POST', data)
  return res
}

export const getDocumentTypes = async (companyId) => {
  const res = await request(`company/${companyId}/documents-types`, 'GET')
  return res
}

export const getDocumentTypesGrouped = async (companyId) => {
  const res = await request(`company/${companyId}/documents-types/grouped-by-category`, 'GET')
  return res
}

export const getDocumentType = async (companyId, documentTypeId) => {
  const res = await request(`company/${companyId}/documents-types/${documentTypeId}`, 'GET')
  return res
}

export const updateDocumentType = async (companyId, documentTypeId, data) => {
  const res = await request(`company/${companyId}/documents-types/${documentTypeId}`, 'PATCH', data)
  return res
}

export const removeDocumentType = async (companyId, documentTypeId) => {
  const res = await request(`company/${companyId}/documents-types/${documentTypeId}`, 'DELETE')
  return res
}

export const createDocument = async (companyId, data) => {
  const res = await request(`company/${companyId}/documents`, 'POST', data)
  return res
}

export const getDocuments = async (companyId, params) => {
  const res = await request(`company/${companyId}/documents`, 'GET', params)
  return res
}

export const getEmployeesDocuments = async (companyId, params) => {
  const res = await request(`company/${companyId}/documents/employees`, 'GET', params)
  return res
}

export const getEmployeeDocuments = async (companyId, employeeId, params) => {
  const res = await request(`company/${companyId}/documents/employees/${employeeId}`, 'GET', params)
  return res
}

export const getAcknowledgmentDocuments = async (companyId) => {
  const res = await request(`company/${companyId}/documents/acknowledgment`, 'GET')
  return res
}

export const getEmployeesOwnedDocuments = async (companyId, params) => {
  const res = await request(`company/${companyId}/documents/employees-owned`, 'GET', params)
  return res
}

export const getEmployeesOwnedDocumentEmployees = async (companyId, documentId, params) => {
  const res = await request(`company/${companyId}/documents/${documentId}/employees-owned/employees`, 'GET', params)
  return res
}

export const getEmployeeDocument = async (companyId, employeeId, documentId) => {
  const res = await request(`company/${companyId}/documents/${documentId}/employees/${employeeId}`, 'GET')
  return res
}

export const getDocument = async (companyId, documentId) => {
  const res = await request(`company/${companyId}/documents/${documentId}`, 'GET')
  return res
}

export const getDocumentOverview = async (companyId, documentId) => {
  const res = await request(`company/${companyId}/documents/${documentId}/overview`, 'GET')
  return res
}

export const getAcknowledgmentEmployees = async (companyId, documentId) => {
  const res = await request(`company/${companyId}/documents/${documentId}/acknowledgment/employees`, 'GET')
  return res
}

export const updateDocument = async (companyId, documentId, data) => {
  const res = await request(`company/${companyId}/documents/${documentId}`, 'PATCH', data)
  return res
}

export const sendDocumentReminder = async (companyId, data) => {
  const res = await request(`company/${companyId}/documents/send-reminder`, 'POST', data)
  return res
}

export const changeReviewStatusDocument = async (companyId, documentId, data) => {
  const res = await request(`company/${companyId}/documents/${documentId}/review-status`, 'PATCH', data)
  return res
}

export const removeDocuments = async (companyId, data) => {
  const res = await request(`company/${companyId}/documents/delete`, 'POST', data)
  return res
}

export const getDocumentEmployees = async (companyId, documentId, params) => {
  const res = await request(`company/${companyId}/documents/${documentId}/employees`, 'GET', params)
  return res
}

export const uploadDocumentForEmployee = async (companyId, employeeId, documentId, data) => {
  const res = await request(`company/${companyId}/documents/${documentId}/employees/${employeeId}/upload`, 'POST', data)
  return res
}

export const sendDocumentOutstandingReminder = async (companyId, documentId) => {
  const res = await request(`company/${companyId}/documents/${documentId}/send-reminder`, 'POST', {})
  return res
}

export const getWeeklyWorkTimesExportData = async (companyId, params) => {
  const res = await request(`company/${companyId}/sheet/work-times`, 'GET', params)
  return res
}

export const extractDataFromInvoice = async (companyId, placeId, file) => {
  const formData = new FormData()
  formData.append('file', file)
  const token = localStorage.getItem('token')
  const res = await fetch(`${BASE_URL}/company/${companyId}/places/${placeId}/expenses/analyze-document`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      // 'Content-Type': 'multipart/form-data',
      'Accept': 'application/json'
    },
    body: formData
  })
  if (res.ok) {
    return await res.json()
  }
  return null
}

export const downloadPlaceDocument = async (companyId, placeId, folderId, documentId) => {
  const res = await fetch(`${BASE_URL}/company/${companyId}/places/${placeId}/folders/${folderId}/documents/${documentId}/download`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  if (res.ok) {
    const blob = await res.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = res.headers.get('Content-Disposition')?.split('filename=')[1] || 'document'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
  return null
}

async function fetchDocumentFile(companyId, documentId, fileId) {
  const response = await fetch(
    `${BASE_URL}/company/${companyId}/documents/${documentId}/files/${fileId}/download`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to download file');
  }

  return response;
}

export async function getDocumentFileBlob(companyId, documentId, fileId) {
  const response = await fetchDocumentFile(companyId, documentId, fileId);
  return response.blob();
}

export async function downloadDocumentFile(companyId, documentId, fileId) {
  const response = await fetchDocumentFile(companyId, documentId, fileId);
  const blob = await response.blob();

  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;

  const contentDisposition =
    response.headers.get('Content-Disposition');

  let fileName = `document-${fileId}`;

  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );

    if (match?.[1]) {
      fileName = match[1].replace(/['"]/g, '');
    }
  }

  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(url);
}

export const fetchExpenseFile = async (companyId, placeId, expenseId) => {
  const res = await fetch(`${BASE_URL}/company/${companyId}/places/${placeId}/expenses/${expenseId}/download`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  if (res.ok) {
    const blob = await res.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = () => reject(new Error('Failed to convert image URL to base64'))
      reader.readAsDataURL(blob)
    })
  }
}

export const downloadExpenseFile = async (companyId, placeId, expenseId) => {
  const res = await fetch(`${BASE_URL}/company/${companyId}/places/${placeId}/expenses/${expenseId}/download`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  if (res.ok) {
    const blob = await res.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = res.headers.get('Content-Disposition')?.split('filename=')[1] || 'document'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
  return null
}

export const uploadPlaceDocuments = async (companyId, placeId, folderId, files, onProgress) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('documents[]', file)
    })
    const token = localStorage.getItem('token')
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}/company/${companyId}/places/${placeId}/folders/${folderId}/documents`, true)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.upload.onprogress = function(event) {
      if (event.lengthComputable) {
        onProgress(event.total, event.loaded)
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        console.log('Upload progress:', percentComplete + '%')
      }
    };
    xhr.onload = function() {
      if (xhr.status === 200) {
        resolve(JSON.parse(xhr.responseText))
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`))
      }
    };
    xhr.send(formData)
  })
}